import { useStore } from '@store';
import type { FeatureConfig } from '@features/userManagement/types';

/** The grant-row mapping lives with the auth services. Re-exported here so the tier-aware
 * permission helpers and the grant readers stay one import for callers. */
export { grantedFeatureNames, grantedFeatureConfigs, type FeatureGrant } from '@features/auth/services/featureGrants';
export type FeatureGrantRow = NonNullable<import('@features/auth/services/featureGrants').FeatureGrant>;

/**
 * Check if user has a specific feature permission, with tier awareness.
 *
 * Logic:
 * 1. Explicit grant → always true
 * 2. No tier data loaded → fallback to grant-only (backward compat)
 * 3. Global feature (no config.tiers) → needs explicit grant
 * 4. Tier-scoped feature with rooftopId → check that rooftop's tier
 * 5. Tier-scoped feature without rooftopId → check if ANY user rooftop matches
 *
 * When a feature's tier list doesn't include the rooftop's tier, the feature
 * is irrelevant for that context and auto-passes (returns true).
 */
export const hasFeaturePermission = (
  userPermissions: string[],
  featureName: string,
  featureConfigs?: Record<string, FeatureConfig | null>,
  customerTierMap?: Record<number, string>,
  rooftopId?: number,
): boolean => {
  // 1. Explicit grant → always true
  if (userPermissions.includes(featureName)) return true;

  // 2. No tier data loaded → fallback to grant-only (backward compat)
  if (!featureConfigs || !customerTierMap || Object.keys(featureConfigs).length === 0) {
    return false;
  }

  // 3. No config or no tiers → global feature, needs grant
  const config = featureConfigs[featureName];
  if (!config?.tiers?.length) return false;

  // 4. RooftopId provided → check that specific rooftop's tier
  if (rooftopId !== undefined) {
    const tier = customerTierMap[rooftopId];
    if (!tier) return false;                    // unknown rooftop → deny
    return !config.tiers.includes(tier);        // tier not in list → irrelevant → true
  }

  // 5. No rooftopId → check if ANY user rooftop has a matching tier
  const userTiers = Object.values(customerTierMap);
  const anyRelevant = userTiers.some(t => config.tiers!.includes(t));
  return !anyRelevant;                          // no matching rooftops → irrelevant → true
};

/**
 * React hook to check if current user has a specific feature permission.
 * No role bypass — all roles (including super-admin) are subject to feature gates.
 *
 * For rooftop-scoped features (dispatching, dispatching-rideshare), pass the
 * rooftopId to check against that specific rooftop's tier.
 */
export const useFeaturePermission = (featureName: string, rooftopId?: number): boolean => {
  const permissions = useStore(state => state.permissions);
  const featureConfigs = useStore(state => state.featureConfigs);
  const customerTierMap = useStore(state => state.customerTierMap);
  return hasFeaturePermission(permissions, featureName, featureConfigs, customerTierMap, rooftopId);
};

/**
 * React hook to check multiple features at once. Returns an array of booleans
 * (one per feature name). Useful when any-of-many grants access.
 */
export const useFeaturePermissions = (featureNames: string[]): boolean[] => {
  const permissions = useStore(state => state.permissions);
  const featureConfigs = useStore(state => state.featureConfigs);
  const customerTierMap = useStore(state => state.customerTierMap);
  return featureNames.map(name =>
    hasFeaturePermission(permissions, name, featureConfigs, customerTierMap),
  );
};

/**
 * React hook to check if permissions have been loaded from the server.
 */
export const usePermissionsLoaded = (): boolean => {
  return useStore(state => state.permissionsLoaded);
};

/**
 * React hook to get all user permissions.
 */
export const useUserPermissions = (): string[] => {
  return useStore(state => state.permissions);
};
